import { logger } from "../utils/logger";
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { TaxStatus } from '../types/tax';
import axios from 'axios';
import { useAuth } from './AuthContext';

interface TaxContextType {
    taxStatus: TaxStatus | null;
    loading: boolean;
    refreshing: boolean;
    error: string | null;
    refreshTaxStatus: () => Promise<void>;
}

const TaxContext = createContext<TaxContextType>({
    taxStatus: null,
    loading: false,
    refreshing: false,
    error: null,
    refreshTaxStatus: async () => {},
});

export const TaxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [taxStatus, setTaxStatus] = useState<TaxStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { isAuthenticated } = useAuth();

    const fetchTaxStatus = useCallback(async () => {
        if (!isAuthenticated) return;
        const useBackgroundRefresh = hasLoadedOnce;
        try {
            if (useBackgroundRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            setError(null);
            const response = await api.get('/api/v1/tax/status');
            setTaxStatus(response.data);
            if (!hasLoadedOnce) {
                setHasLoadedOnce(true);
            }
        } catch (err) {
            if (axios.isAxiosError(err)) {
                if (err.response?.status === 403) {
                    setError('Authentication required. Please log in again.');
                } else if (err.response?.status === 404) {
                    setError('Tax status endpoint not found. Please check API configuration.');
                } else {
                    setError(`Failed to fetch tax status: ${err.message}`);
                }
            } else {
                setError('An unexpected error occurred while fetching tax status');
            }
            logger.error('Error fetching tax status:', err);
        } finally {
            if (useBackgroundRefresh) {
                setRefreshing(false);
            } else {
                setLoading(false);
            }
        }
    }, [hasLoadedOnce, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated || hasLoadedOnce) {
            return;
        }
        void fetchTaxStatus();
    }, [isAuthenticated, hasLoadedOnce, fetchTaxStatus]);

    return (
        <TaxContext.Provider
            value={{
                taxStatus,
                loading,
                refreshing,
                error,
                refreshTaxStatus: fetchTaxStatus,
            }}
        >
            {children}
        </TaxContext.Provider>
    );
};

export const useTax = () => useContext(TaxContext); 
